/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, DataTable, HFlow, Icon, Modal, ModalBody, ModalFooter, Text, Tooltip, VFlow } from 'bold-ui'
import { gray } from 'bold-ui/lib/styles/colors'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Box } from 'components/Box'
import { FooterButton } from 'components/footer-button'
import { usePecField } from 'components/form/final-form/hooks/useField'
import {
  AtestadoSdcDto,
  CondutaSdc,
  PrescricaoSdcDto,
  ProcedimentoSdcDto,
  ReceitaMedicamentoSdcDto,
} from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useAlertasSdc } from 'hooks/sdc/useAlertasSdc'
import { useEffect, useState } from 'react'
import { MetaPath } from 'util/metaPath'

import { ReactComponent as SdcIcon } from '../../../../../../images/suporte-decisao-clinica/health-system-outline.svg'
import AtestadoModel from '../atestados/components/model-atestado'
import { SolicitacaoExameModel } from '../exames/model-solicitacaoExames'
import { OrientacaoFormModel } from '../orientacoes/types/OrientacoesModel'
import { PlanoFormModel } from '../PlanoForm'
import { MedicamentoFormModel } from '../prescricao-medicamento/model-prescricao'
import {
  converterAtestadoSdcToAtestadoModel,
  converterExameSdcToSolicitacaoExameModel,
  converterReceitaMedicamentoSdcDtoToMedicamentoFormModel as converterReceitaMedicamentoSdcToMedicamentoFormModel,
} from '../sdc/converter-sdc'
import { ExameSdcCheckbox } from './ExameSdcCheckbox'
import { SdcPrescricaoGroup } from './SdcPrescricaoGroup'

interface SdcCondutaModalProps {
  modalOpen: boolean
  setModalOpen(boolean): void
  name: MetaPath<PlanoFormModel>
}

export default function SdcCondutaModal(props: SdcCondutaModalProps) {
  const { modalOpen, setModalOpen, name } = props
  const [condutaModal, setCondutaModal] = useState<CondutaSdc>()

  const [mapMedicamentosParaPrescricao, setListaMedicamentosParaPrescricao] = useState<
    Map<string, ReceitaMedicamentoSdcDto>
  >(new Map())

  const { cidadao, iniciadoEm } = useAtendimentoContext()

  const handleTogglePrescricao = (prescricao: ReceitaMedicamentoSdcDto) => {
    setListaMedicamentosParaPrescricao((prevMap) => {
      const novoMap = new Map(prevMap)

      if (novoMap.has(prescricao.medicamentoCatmat.codigoCatMat)) {
        novoMap.delete(prescricao.medicamentoCatmat.codigoCatMat)
      } else {
        novoMap.set(prescricao.medicamentoCatmat.codigoCatMat, prescricao)
      }

      return novoMap
    })
  }

  const [mapExamesParaPrescricao, setMapExamesParaPrescricao] = useState(new Map<string, ProcedimentoSdcDto>())

  const gruposProcedimentos = condutaModal?.examesSdc?.gruposProcedimentosSdcDto

  useEffect(() => {
    if (gruposProcedimentos) {
      const examesPreviamentePrescritos = new Map(
        gruposProcedimentos
          .flatMap((grupo) => grupo.procedimentosSdcDto)
          .filter((proc) => proc.previamentePrescrito)
          .map((proc) => [proc.exameSolicitado.codigo, proc])
      )

      setMapExamesParaPrescricao(examesPreviamentePrescritos)
    }
  }, [gruposProcedimentos])

  const handleToggleProcedimento = (procedimento: ProcedimentoSdcDto) => {
    setMapExamesParaPrescricao((prevMap) => {
      const novoMap = new Map(prevMap)
      const codigo = procedimento.exameSolicitado.codigo

      if (novoMap.has(codigo)) {
        novoMap.delete(codigo)
      } else {
        novoMap.set(codigo, {
          ...procedimento,
          previamentePrescrito: true,
        })
      }

      return novoMap
    })
  }

  const {
    acesso: { unidadeSaude },
  } = useAcessoLotacaoOrEstagio()

  const { setGrupoDengue, condutasSdc, hasCondutaAplicada, marcarCondutaComoAplicada } = useAlertasSdc()
  const alert = useAlert()

  const { analytics } = useFirebase()

  useEffect(() => {
    const condutaSdcModal = condutasSdc.find((conduta) => conduta.tipo === 'MODAL')
    if (condutaSdcModal) {
      setCondutaModal(condutaSdcModal)
      setModalOpen(true)
    }
  }, [condutasSdc, setModalOpen])

  const medicamentosFormField = usePecField({ name: name.prescricaoMedicamento.medicamentos.absolutePath() })
  const orientacaoFormField = usePecField({ name: name.orientacao.absolutePath() })
  const examesFormField = usePecField({ name: name.solicitacoesExame.absolutePath() })
  const atestadosFormField = usePecField({ name: name.atestado.atestadosRecentes.absolutePath() })

  if (!condutaModal) return null

  const handleModalClose = () => {
    setModalOpen(false)
    setGrupoDengue(null)
    setCondutaModal(null)
    setListaMedicamentosParaPrescricao(new Map())
    setMapExamesParaPrescricao(new Map())
  }

  const handleAplicarConduta = () => {
    if (hasCondutaAplicada || medicamentosFormField?.input?.value?.length > 0) return

    const listaPrescricoes = Array.from(mapMedicamentosParaPrescricao.values())

    const medicamentoAutomatico: MedicamentoFormModel[] = listaPrescricoes
      .map(converterReceitaMedicamentoSdcToMedicamentoFormModel)
      .filterNotFalsy()

    const orientacaoAutomatica: OrientacaoFormModel = { descricao: condutaModal.observacao }

    const exameAutomatico: SolicitacaoExameModel = converterExameSdcToSolicitacaoExameModel(
      condutaModal.examesSdc,
      mapExamesParaPrescricao
    )

    const atestadosAutomaticos: AtestadoModel[] = condutaModal.atestadosSdc?.map((atestado) =>
      converterAtestadoSdcToAtestadoModel(atestado, unidadeSaude, cidadao, iniciadoEm)
    )

    medicamentosFormField.input.onChange(medicamentoAutomatico)
    orientacaoFormField.input.onChange([...orientacaoFormField.input.value, orientacaoAutomatica])
    exameAutomatico.examesSolicitados.length > 0 &&
      examesFormField.input.onChange([...examesFormField.input.value, exameAutomatico])
    atestadosAutomaticos &&
      atestadosFormField.input.onChange([...atestadosFormField.input.value, ...atestadosAutomaticos])

    analytics.logEvent('aplicar_conduta_recomendada')
    alert('success', 'Conduta recomendada aplicada com sucesso.')
    marcarCondutaComoAplicada(condutaModal)
    handleModalClose()
  }

  return (
    <Modal open={modalOpen} onClose={handleModalClose}>
      <ModalBody>
        <VFlow vSpacing={1.25}>
          <VFlow vSpacing={0.5}>
            <HFlow alignItems='center'>
              <Icon icon={SdcIcon} size={2.5} />
              <Text variant='h1' fontWeight='bold'>
                Suporte à Decisão Clínica
              </Text>
            </HFlow>
            <VFlow vSpacing={0}>
              <HFlow alignItems='center'>
                <Text variant='h2' color='primary'>
                  {condutaModal.titulo + ' - Grupo ' + condutaModal.grupoDengue}
                </Text>
                {condutaModal.diferencial && (
                  <Tooltip text={condutaModal.diferencial} style={styles.tooltip}>
                    <Icon icon='infoCircleFilled' color={gray.c20} />
                  </Tooltip>
                )}
              </HFlow>
              <Text color='primary' fontSize={1}>
                {condutaModal.subtitulo}
              </Text>
            </VFlow>
            <Text>
              Ao aplicar a conduta recomendada, o PEC realizará <Text component='strong'>automaticamente</Text> as
              seguintes ações:
            </Text>
          </VFlow>
          {condutaModal.prescricoes?.isNotEmpty() && (
            <VFlow>
              <VFlow vSpacing={0.5}>
                <Text component='strong'>
                  A aba "Prescrição de medicamentos" será preenchida com os seguintes itens
                  {cidadao.idadeEmAnos <= 12 && ', de acordo com a faixa etária e/ou peso do cidadão'}:
                </Text>
                <Alert type='info' styles={{ wrapper: styles.alertContainer }}>
                  <Text color='primary' component='p'>
                    Selecione os medicamentos que deseja prescrever para o cidadão. Após aplicar a conduta,{' '}
                    <Text color='primary' fontWeight='bold'>
                      é possível ajustar a prescrição
                    </Text>
                    , se necessário.
                  </Text>
                </Alert>
                <VFlow vSpacing={0.5}>
                  {condutaModal.prescricoes.map((prescricao: PrescricaoSdcDto, index) => (
                    <SdcPrescricaoGroup
                      key={index}
                      prescricao={prescricao}
                      handleTogglePrescricao={handleTogglePrescricao}
                      idadeCidadaoEmAnos={cidadao.idadeEmAnos}
                    />
                  ))}
                </VFlow>
              </VFlow>
            </VFlow>
          )}
          {condutaModal.examesSdc && (
            <VFlow>
              <Text component='strong'>
                A aba “Solicitação de exames/procedimentos” será preenchida com os seguintes itens:
              </Text>
              <VFlow vSpacing={0.5}>
                {condutaModal.examesSdc.gruposProcedimentosSdcDto.map((grupo) => (
                  <ExameSdcCheckbox
                    dataTestId='ExameSdcCheckbox.exame'
                    key={grupo.procedimentosSdcDto[0].exameSolicitado.codigo}
                    grupoExames={grupo}
                    handleToggleProcedimento={handleToggleProcedimento}
                    examesPreviamentePrescritos={mapExamesParaPrescricao}
                  />
                ))}
              </VFlow>
            </VFlow>
          )}

          {condutaModal.observacao && (
            <VFlow>
              <Text component='strong'>A aba "Orientações" será preenchida com o seguinte texto:</Text>
              <Box>
                <Text dangerouslySetInnerHTML={{ __html: condutaModal.observacao }} />
              </Box>
            </VFlow>
          )}

          {condutaModal.atestadosSdc?.length > 0 && (
            <VFlow>
              <Text component='strong'>A aba "Atestado" será preenchida com os seguintes itens:</Text>
              <VFlow vSpacing={0.5}>
                <DataTable<AtestadoSdcDto>
                  rows={condutaModal.atestadosSdc}
                  columns={[
                    {
                      name: 'cid10',
                      header: 'CID10',
                      render: (row) => row.cid10.nome + ' - ' + row.cid10.codigo,
                      style: css`
                        width: 50%;
                      `,
                    },
                    { name: 'dias', header: 'Dias', render: (row) => row.dias },
                  ]}
                />
              </VFlow>
            </VFlow>
          )}

          <Alert type='info' styles={{ wrapper: styles.alertContainer }}>
            <Text color='primary'>
              Após aplicar a conduta,{' '}
              <Text color='primary' fontWeight='bold'>
                revise os registros feitos automaticamente.{' '}
              </Text>
              Se necessário, edite-os antes de imprimir e entregar ao cidadão.
            </Text>
          </Alert>
        </VFlow>
      </ModalBody>
      <ModalFooter>
        <HFlow justifyContent='flex-end'>
          <FooterButton onClick={handleModalClose}>Cancelar</FooterButton>
          <FooterButton kind='primary' onClick={handleAplicarConduta} data-testid='FooterButton.condutaRecomendada'>
            Aplicar conduta recomendada
          </FooterButton>
        </HFlow>
      </ModalFooter>
    </Modal>
  )
}

const styles = {
  alertContainer: css`
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;

    & li {
      margin-left: 0.25rem;
      display: block;
    }

    & li::before {
      content: '•';
      display: inline-flex;
      width: 1ch;
      margin-right: 0.5rem;
    }
  `,
  tooltip: css`
    width: 22.125rem;
    max-width: 22.125rem;
  `,
}
